/**
 * Image work for the gallery upload, all in one worker and one job at a time: three 48 MP
 * photos decoded at once can exhaust a phone's memory, so a big decode never overlaps another.
 *
 * - `makeThumbnail`: a ~320 px JPEG for the previews. Thumbnails jump ahead of downscales, so
 *   the dialog fills in while the queue optimizes.
 * - `downscaleImage`: long side up to 3840 px, re-encoded at 0.85. A 50 MB camera photo becomes
 *   a few MB, which is what makes a bulk upload bearable on a phone. GIFs (animation) and files
 *   that are already small are sent as they are, and any failure (old browser, odd file) falls
 *   back to the original: optimizing is never the reason an upload fails.
 *
 * The worker is terminated after a short idle period, which frees whatever it still holds.
 */

const MAX_SIDE = 3840
const QUALITY = 0.85
/** At or under this size and resolution, re-encoding would not pay off. */
const SMALL_ENOUGH_BYTES = 2 * 1024 * 1024
const THUMBNAIL_SIDE = 320
const THUMBNAIL_QUALITY = 0.7
const IDLE_TERMINATE_MS = 15_000

type WorkerMessage =
  | { kind: 'thumbnail'; file: Blob; maxSide: number; quality: number }
  | { kind: 'downscale'; file: Blob; maxSide: number; type: 'image/jpeg' | 'image/webp'; quality: number; keepUnderBytes: number }

interface Job {
  message: WorkerMessage
  /** Lower runs first: thumbnails (0) before downscales (1). */
  priority: number
  resolve: (blob: Blob | null) => void
}

let worker: Worker | null = null
let workerBroken = false
let nextId = 0
let running: { id: number; job: Job } | null = null
const jobs: Job[] = []
let idleTimer: ReturnType<typeof setTimeout> | undefined

const supported = () =>
  typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined' && typeof createImageBitmap !== 'undefined'

const terminate = () => {
  worker?.terminate()
  worker = null
}

const finish = (id: number, blob: Blob | null) => {
  if (!running || running.id !== id) return
  const { job } = running
  running = null
  job.resolve(blob)
  runNext()
}

const getWorker = (): Worker | null => {
  if (workerBroken) return null
  if (worker) return worker
  try {
    worker = new Worker(new URL('./imageDownscale.worker.ts', import.meta.url), { type: 'module' })
    worker.onmessage = (event: MessageEvent<{ id: number; blob: Blob | null }>) => finish(event.data.id, event.data.blob)
    worker.onerror = () => {
      workerBroken = true
      terminate()
      if (running) finish(running.id, null)
    }
    return worker
  } catch {
    workerBroken = true
    return null
  }
}

function runNext() {
  if (running) return
  if (jobs.length === 0) {
    if (!idleTimer) idleTimer = setTimeout(() => { idleTimer = undefined; if (!running && jobs.length === 0) terminate() }, IDLE_TERMINATE_MS)
    return
  }
  if (idleTimer) { clearTimeout(idleTimer); idleTimer = undefined }
  jobs.sort((a, b) => a.priority - b.priority)
  const job = jobs.shift() as Job
  const target = getWorker()
  if (!target) {
    // Resolved after the caller awaits, like a worker reply.
    queueMicrotask(() => { job.resolve(null); runNext() })
    return
  }
  const id = nextId++
  running = { id, job }
  target.postMessage({ ...job.message, id })
}

const enqueue = (message: WorkerMessage, priority: number): Promise<Blob | null> =>
  new Promise((resolve) => {
    jobs.push({ message, priority, resolve })
    runNext()
  })

/** A small JPEG of the image for previews, or null when the browser cannot make one. */
export const makeThumbnail = async (file: File): Promise<Blob | null> => {
  if (!supported()) return null
  return enqueue({ kind: 'thumbnail', file, maxSide: THUMBNAIL_SIDE, quality: THUMBNAIL_QUALITY }, 0)
}

/** The file to upload: a smaller copy when it is worth it, otherwise the original. */
export const downscaleImage = async (file: File, signal?: AbortSignal): Promise<File> => {
  if (signal?.aborted || file.type === 'image/gif' || !supported()) return file
  // PNG and WebP may carry transparency, which JPEG would paint over.
  const type: 'image/jpeg' | 'image/webp' = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/webp'
  const blob = await enqueue({ kind: 'downscale', file, maxSide: MAX_SIDE, type, quality: QUALITY, keepUnderBytes: SMALL_ENOUGH_BYTES }, 1)
  if (!blob || blob.size >= file.size) return file
  const name = file.name.replace(/\.[^/.]+$/, '') + (type === 'image/jpeg' ? '.jpg' : '.webp')
  return new File([blob], name, { type, lastModified: file.lastModified })
}
